import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SpeedIcon from '@mui/icons-material/Speed';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { ReactComponent as EdgeServices } from 'src/assets/icons/EdgeServices.svg';
import {
  DataTableActionsButton,
  DataTableActionsWrapper,
} from 'src/components/DataTable';
import { PageCardLoader } from 'src/components/PageCardLayout';
import { isModalComplete } from 'src/components/ModalDialog';
import { useModal } from 'src/hooks';
import { useStoreUnitActions } from 'src/redux/helpers';
import { useUserRoles } from 'src/auth';
import { useSolutionUserRoles } from '../../hooks';
import solutionsStore, { useSolutionDetails } from '../../redux';
import { SiteClusters, SolutionSiteItem } from '../../types';
import SolutionSiteDeleteModal from './SolutionSiteDeleteModal';
import SolutionNamespaceRscLimitViewModal from './SolutionNamespaceRscLimitViewModal';
import SolutionSiteServiceUrlViewModal from './SolutionSiteServiceUrlViewModal';
import SolutionSiteServiceUrlCreateModal from './SolutionSiteServiceUrlCreateModal';

type Props = {
  item?: SolutionSiteItem;
};

function SolutionSiteListAction({ item }: Props) {
  const actions = useStoreUnitActions(solutionsStore);
  const { solutionId } = useParams();
  const [data, loading] = useSolutionDetails();
  const [edgeService, setEdgeService] = useState(false);
  const [serviceUrl, setServiceUrl] = useState<any>('');
  const siteId: any = item?.id;
  const { isPlatformEngineer, isPlatformOwner, isPlatformReader } =
    useUserRoles();
  const { isSolutionEditor } = useSolutionUserRoles(solutionId);
  const isValidUser = isPlatformEngineer || isPlatformOwner || isPlatformReader;
  const isValidOwner = isPlatformEngineer || isPlatformOwner;

  useEffect(() => {
    const url: SiteClusters | undefined = data?.siteClusters?.find(
      (cluster) => cluster?.siteId === siteId
    );
    setServiceUrl(url?.nanoMQServiceUrl);
    const edgeUrl: any = Array.isArray(url?.nanoMQServiceUrl)
      ? url?.nanoMQServiceUrl.some(
          (detail) => detail !== undefined && detail !== null && detail !== ''
        )
      : url?.nanoMQServiceUrl !== undefined &&
        url?.nanoMQServiceUrl !== null &&
        url?.nanoMQServiceUrl !== '';
    setEdgeService(edgeUrl);
  }, [data]);

  useEffect(() => {
    actions.details(solutionId);
  }, [solutionId]);

  const onClose = (event, reason) => {
    isModalComplete(reason);
    actions.sites(solutionId);
  };

  const [DeleteDialog, handleDelete] = useModal(SolutionSiteDeleteModal, {
    id: item?.id,
    name: item?.name,
    onClose,
  });
  const [NamespaceRscLimitDialog, showNamespaceRscLimitDialog] = useModal(
    SolutionNamespaceRscLimitViewModal,
    {
      siteId: item?.id,
      siteName: item?.name,
      clusterIds: item?.clusters?.map((cluster) => cluster.id),
      clusters: item?.clusters?.map((cluster) => ({
        id: cluster.id,
        clusterProviderType: cluster.clusterProviderType,
      })),
      onClose,
    }
  );

  const [edgeServicesUrlDialog, showEdgeServicesUrlDialog] = useModal(
    SolutionSiteServiceUrlCreateModal,
    {
      siteId: item?.id,
      siteName: item?.name,
    }
  );

  const [edgeServicesUrlViewDialog, showEdgeServicesUrlViewDialog] = useModal(
    SolutionSiteServiceUrlViewModal,
    {
      siteId: item?.id,
      siteName: item?.name,
      item: serviceUrl,
      edgeServiceUrl: serviceUrl,
      setServiceUrl,
    }
  );

  const handleShowEdgeService = () => {
    actions.details(solutionId);
    if (edgeService && serviceUrl) {
      showEdgeServicesUrlViewDialog();
    } else {
      showEdgeServicesUrlDialog();
    }
  };

  return (
    <DataTableActionsWrapper>
      <DataTableActionsButton
        data-testid="solution-site-edge-services"
        icon={EdgeServices}
        title="NanoMQ Service URL"
        onClick={handleShowEdgeService}
        sx={{ paddingTop: '15px', paddingLeft: '10px' }}
        disabled={!isSolutionEditor && !edgeService}
      />
      <DataTableActionsButton
        data-testid="solution-site-namespace-resource-limit"
        icon={SpeedIcon}
        title="Quota Limits"
        onClick={showNamespaceRscLimitDialog}
        disabled={!isValidUser}
      />
      <DataTableActionsButton
        data-testid="solution-sites-item-delete-action"
        icon={DeleteOutlinedIcon}
        title="Delete"
        onClick={handleDelete}
        disabled={!isValidOwner}
      />
      {edgeServicesUrlViewDialog}
      {edgeServicesUrlDialog}
      {NamespaceRscLimitDialog}
      {DeleteDialog}
      {loading && <PageCardLoader />}
    </DataTableActionsWrapper>
  );
}

export default SolutionSiteListAction;
